import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import {
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Flex,
  MultiSelect,
  NumberInput,
  Radio,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useWorkerOptions } from '@worker/hooks/useWorkerData';
import { Controller, Form, useForm } from 'react-hook-form';
import {
  AdvancedFiltersFormValues,
  DEFAULT_ADVANCED_FILTERS,
} from '@worker/stores/types';
import { useWorkerStore } from '@worker/stores';
import { handleMyJobsSearch } from '@worker/helpers/my-jobs-helpers';
import { CountryName } from '@th-types/address.type';
import { MY_JOBS_PAGE_SIZE } from '@constants/various';

interface AdvancedFiltersProps {
  onClose?: () => void;
  onApply?: (values: AdvancedFiltersFormValues) => void;
}

export default function AdvancedFilters({
  onClose,
  onApply,
}: AdvancedFiltersProps) {
  const {
    searchFilters,
    advancedFilters,
    setAdvancedFilters,
    resetAdvancedFilters,
  } = useWorkerStore();

  const [searchParams, setSearchParams] = useSearchParams();

  const { control, handleSubmit, watch, reset } =
    useForm<AdvancedFiltersFormValues>({
      defaultValues: advancedFilters,
    });

  // Sync form with store state
  useEffect(() => {
    reset(advancedFilters);
  }, [advancedFilters, reset]);

  const { data: workerProfileAllowedOptions } = useWorkerOptions();

  const minHourlyEnabled = watch('minHourlyEnabled');
  const minDailyEnabled = watch('minDailyEnabled');
  const onGoing = watch('onGoing');

  const submit = (values: AdvancedFiltersFormValues) => {
    // Save to store
    setAdvancedFilters(values);

    // Update URL with both search filters and advanced filters
    handleMyJobsSearch(searchFilters, values, setSearchParams, {
      itemsPerPage: MY_JOBS_PAGE_SIZE,
      existingSearchParams: searchParams,
    });

    // Call legacy callback
    onApply?.(values);
  };

  const handleClear = () => {
    // Reset store
    resetAdvancedFilters();
    reset(DEFAULT_ADVANCED_FILTERS);

    // Update URL with only search filters (removes advanced filters)
    handleMyJobsSearch(
      searchFilters,
      DEFAULT_ADVANCED_FILTERS,
      setSearchParams,
      {
        itemsPerPage: MY_JOBS_PAGE_SIZE,
        existingSearchParams: searchParams,
      }
    );
  };

  return (
    <Stack gap="sm" w="100%">
      <Flex justify="space-between" align="center">
        <Title order={4}>Advanced Filters</Title>
        <ActionIcon
          variant="transparent"
          onClick={onClose}
          color="var(--black)"
        >
          <FiX size={24} />
        </ActionIcon>
      </Flex>
      <Divider />

      <Form
        control={control}
        style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
      >
        {/* Job Start Date */}
        <Controller
          name="startDate"
          control={control}
          render={({ field }) => (
            <DateInput
              label="Job Start Date"
              value={field.value}
              onChange={field.onChange}
              placeholder="MM / DD / YYYY"
              valueFormat="MM / DD / YYYY"
              required
              clearable
            />
          )}
        />

        {/* Job End Date */}
        <Controller
          name="endDate"
          control={control}
          render={({ field }) => (
            <DateInput
              label="Job End Date"
              value={field.value}
              onChange={field.onChange}
              placeholder="MM / DD / YYYY"
              valueFormat="MM / DD / YYYY"
              disabled={onGoing}
              required
              clearable
            />
          )}
        />

        {/* On-Going */}
        <Controller
          name="onGoing"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onChange={field.onChange}
              label="On-Going"
              radius="sm"
            />
          )}
        />

        {/* Job Location */}
        <Title order={6}>Job Location</Title>
        <Flex gap="md" align="flex-start">
          <Controller
            name="jobLocation.includeNational"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onChange={field.onChange}
                label="Include National Jobs"
                radius="sm"
                styles={{
                  label: { fontWeight: 400 },
                }}
              />
            )}
          />
          <Flex direction="column" gap="xs">
            <Controller
              name="jobLocation.country"
              control={control}
              render={({ field }) => (
                <>
                  <Radio
                    label="USA Jobs"
                    value={CountryName.UNITED_STATES}
                    checked={field.value === CountryName.UNITED_STATES}
                    onChange={() => field.onChange(CountryName.UNITED_STATES)}
                  />
                  <Radio
                    label="Canada Jobs"
                    value={CountryName.CANADA}
                    checked={field.value === CountryName.CANADA}
                    onChange={() => field.onChange(CountryName.CANADA)}
                  />
                </>
              )}
            />
          </Flex>
        </Flex>

        {/* Job Titles */}
        <Controller
          name="jobTitles"
          control={control}
          render={({ field }) => (
            <MultiSelect
              label="Job Titles"
              placeholder="Select job titles"
              checkIconPosition="right"
              value={field.value}
              onChange={field.onChange}
              searchable
              clearable
              styles={{ input: { padding: '8px', display: 'flex' } }}
              data={workerProfileAllowedOptions?.jobTitles.map(
                (jobTitle: { id: number; name: string }) => ({
                  label: jobTitle.name,
                  value: String(jobTitle.id),
                })
              )}
            />
          )}
        />

        {/* Job Rates */}
        <Title order={6}>Job Rates</Title>
        <Controller
          name="minHourlyEnabled"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onChange={field.onChange}
              label="Minimum Hourly Rate"
              radius="sm"
            />
          )}
        />
        <Controller
          name="minHourlyRate"
          control={control}
          render={({
            field: { value, onChange, onBlur, name, ref, ...rest },
          }) => (
            <NumberInput
              {...rest}
              name={name}
              ref={ref}
              min={1}
              max={100}
              clampBehavior="strict"
              value={value ?? undefined}
              onChange={(v) => onChange(typeof v === 'number' ? v : undefined)}
              onBlur={onBlur}
              leftSection={
                <Text px="xs" fs="italic">
                  $
                </Text>
              }
              hideControls
              disabled={!minHourlyEnabled}
              aria-label="Enter hourly rate"
            />
          )}
        />

        <Controller
          name="minDailyEnabled"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onChange={field.onChange}
              label="Minimum Daily Rate"
              radius="sm"
            />
          )}
        />
        <Controller
          name="minDailyRate"
          control={control}
          render={({
            field: { value, onChange, onBlur, name, ref, ...rest },
          }) => (
            <NumberInput
              {...rest}
              name={name}
              ref={ref}
              min={1}
              max={1000}
              clampBehavior="strict"
              value={value ?? undefined}
              onChange={(v) => onChange(typeof v === 'number' ? v : undefined)}
              onBlur={onBlur}
              leftSection={
                <Text px="xs" fs="italic">
                  $
                </Text>
              }
              hideControls
              disabled={!minDailyEnabled}
              aria-label="Enter daily rate"
            />
          )}
        />

        <Button variant="white" tt="capitalize" onClick={handleClear}>
          Clear
        </Button>
        <Button tt="capitalize" onClick={handleSubmit(submit)}>
          Apply
        </Button>
      </Form>
    </Stack>
  );
}
